import { useEffect, useRef, useState } from 'react';
import { ApiError } from '../api/client';
import { useFeather } from '../shell/useFeather';
import type { AudienceSegment, CampaignTemplate, MailingApi, Sender } from '../api/mailing';
import {
  DEFAULT_HTML, SPAM_KEYWORDS, VAR_GROUPS, composeBody, renderPreview, spamAudit, splitPreheader, validateHtml,
  type ValidationResult,
} from './mailing-content';
import { PromoNewsEditor } from './PromoNewsEditor';
import { EmailVisualEditor } from './EmailVisualEditor';
import { EmailBuilder, fileToDataUrl } from './EmailBuilder';
import { applyBlocks, blockIssues, hasEditableRegion, newBlock, type EmailBlock } from './email-blocks';
import {
  PROMO_SUBJECT_PREFIX, buildPromoHtml, isPromoTemplate, newPromoItem, promoIssues, promoLang,
  type PromoItem, type PromoMeta,
} from './promo-news';

const WZ_CSS = `
.wz-stepper{display:flex;align-items:center;justify-content:center;gap:14px;padding:14px 0;}
.wz-step{display:flex;align-items:center;gap:8px;opacity:.5;transition:opacity .2s;}
.wz-step.active,.wz-step.done{opacity:1;}
.wz-step-num{width:32px;height:32px;border-radius:50%;background:var(--bs-tertiary-bg);color:var(--bs-secondary-color);display:flex;align-items:center;justify-content:center;font-weight:700;border:2px solid var(--bs-border-color);}
.wz-step.active .wz-step-num{background:var(--bs-primary);color:#fff;border-color:var(--bs-primary);}
.wz-step.done .wz-step-num{background:#22c55e;color:#fff;border-color:#22c55e;}
.wz-step-label{font-size:.86rem;font-weight:500;color:var(--bs-body-color);}
.wz-step-line{flex:0 0 60px;height:2px;background:var(--bs-border-color);}
.wz-lbl{font-size:.74rem;color:var(--bs-secondary-color);font-weight:600;margin-bottom:2px;display:flex;align-items:center;gap:6px;flex-wrap:wrap;}
.wz-hint{font-weight:400;font-size:.7rem;color:var(--bs-secondary-color);text-transform:none;letter-spacing:0;}
.wz-hint code{font-size:.68rem;background:var(--bs-tertiary-bg);padding:1px 4px;border-radius:3px;}
.wz-recipients{font-size:.84rem;padding:8px 12px;background:rgba(0,113,227,.06);border:1px solid rgba(0,113,227,.18);color:#0058b3;border-radius:8px;}
.wz-validation{padding:12px 14px;border-radius:8px;font-size:.84rem;}
.wz-validation.ok{background:rgba(52,199,89,.08);border:1px solid rgba(52,199,89,.3);color:#1e7a36;}
.wz-validation.err{background:rgba(200,43,35,.08);border:1px solid rgba(200,43,35,.3);color:#991b1b;}
.wz-validation.warn{background:rgba(255,149,0,.08);border:1px solid rgba(255,149,0,.3);color:#a35a00;}
.wz-validation ul{margin:6px 0 0 0;padding-left:20px;}
.wz-vars-toolbar{display:flex;flex-wrap:wrap;gap:14px;padding:8px 10px;background:var(--bs-tertiary-bg);border:1px solid var(--bs-border-color-translucent);border-radius:8px;align-items:center;}
.wz-vars-label{font-size:.7rem;color:var(--bs-secondary-color);text-transform:uppercase;letter-spacing:.03em;font-weight:600;}
.wz-var-btn{font-family:ui-monospace,Menlo,monospace;font-size:.7rem;padding:3px 8px;border-radius:5px;background:#fff;color:#0058b3;border:1px solid rgba(0,113,227,.25);cursor:pointer;transition:background .12s,color .12s;white-space:nowrap;}
.wz-var-btn:hover{background:#0058b3;color:#fff;border-color:#0058b3;}
.wz-var-btn.special{color:#a35a00;border-color:rgba(255,149,0,.3);}
.wz3-card{max-width:760px;margin:0 auto;}
.campaign-wizard{max-width:1180px;margin:0 auto}.campaign-wizard-head{display:flex;align-items:flex-end;justify-content:space-between;gap:24px;margin-bottom:22px}.campaign-wizard-head h1{margin:3px 0 5px;font-size:38px;line-height:1.08;letter-spacing:-.035em;font-weight:750}.campaign-wizard-head p{margin:0;color:#747d84;font-size:14px}.campaign-wizard .wz3-card{border-radius:20px!important}.campaign-wizard .wz3-card>.card-body{padding:28px!important}.campaign-wizard .wz-stepper{margin:0 auto 26px!important;padding:0;max-width:760px}.campaign-wizard .wz-step-num{width:36px;height:36px;background:#f3f4f4;border-color:#e0e3e5}.campaign-wizard .wz-step.active .wz-step-num{background:var(--p1-green);border-color:var(--p1-green)}.campaign-wizard .wz-step.done .wz-step-num{background:#202225;border-color:#202225}.campaign-wizard .wz-step-line{background:#e3e6e8}.campaign-wizard .wz-lbl{margin:0 0 7px;color:#5f6970;font-size:12px;letter-spacing:.045em;text-transform:uppercase}.campaign-wizard .row.g-2{--bs-gutter-x:18px;--bs-gutter-y:18px}.campaign-type-backdrop{position:fixed;inset:0;background:rgba(18,24,29,.48);backdrop-filter:blur(8px);z-index:1050;display:flex;align-items:center;justify-content:center;padding:20px}.campaign-type-dialog{max-width:760px;width:100%;border-radius:22px!important}.campaign-type-dialog .card-header{padding:22px 25px!important}.campaign-type-dialog>.card-body{padding:25px!important}
.wz3-card-head{display:flex;justify-content:space-between;align-items:center;gap:10px;margin-bottom:10px;padding-bottom:10px;border-bottom:1px solid var(--bs-border-color-translucent);flex-wrap:wrap;}
.wz3-card-title{display:flex;align-items:center;gap:8px;font-size:.94rem;font-weight:600;}
.wz-summary dt{font-size:.74rem;color:var(--bs-secondary-color);text-transform:uppercase;letter-spacing:.03em;font-weight:600;margin-top:10px;}
.wz-summary dt:first-child{margin-top:0;}
.wz-summary dd{margin:0 0 2px;font-size:.9rem;}
`;

const FLAG: Record<string, string> = { CZ: '🇨🇿', SK: '🇸🇰', RO: '🇷🇴' };

type Step = 1 | 2 | 3;

function toLocalInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export type EditorMode = 'builder' | 'editor';

export function MailingCampaignWizard({ api, eyebrow, countries, onDone, editId, templateId: initialTemplateId, mode: initialMode }: {
  api: MailingApi; eyebrow: string; countries: readonly string[]; onDone: () => void; editId?: number; templateId?: number; mode?: EditorMode;
}) {
  const [step, setStep] = useState<Step>(1);
  const [showSpamWords, setShowSpamWords] = useState(false);
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [preheader, setPreheader] = useState('');
  const [senderId, setSenderId] = useState<number | ''>('');
  const [segmentIds, setSegmentIds] = useState<number[]>([]);
  const [templateId, setTemplateId] = useState<number | ''>('');
  const [scheduleOn, setScheduleOn] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');
  const [html, setHtml] = useState(DEFAULT_HTML);
  const [segments, setSegments] = useState<AudienceSegment[]>([]);
  const [senders, setSenders] = useState<Sender[]>([]);
  const [templates, setTemplates] = useState<CampaignTemplate[]>([]);
  const [editorMode, setEditorMode] = useState<EditorMode>(initialMode ?? 'editor');
  const [mailTab, setMailTab] = useState<'builder' | 'editor' | 'kod' | 'nahled'>(initialMode === 'builder' ? 'builder' : 'editor');
  const [blocks, setBlocks] = useState<EmailBlock[]>(() => [newBlock('text')]);
  const [builderUsed, setBuilderUsed] = useState(initialMode === 'builder');
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [testEmail, setTestEmail] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [prevDevice, setPrevDevice] = useState<'desktop' | 'mobil'>('desktop');
  const [estimate, setEstimate] = useState<{ count: number; unsubscribed: number; missingVocative: number } | null>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const htmlRef = useRef<HTMLTextAreaElement>(null);
  const [promo, setPromo] = useState(false);
  const [promoLoc, setPromoLoc] = useState<string>('CZ');
  const [promoItems, setPromoItems] = useState<PromoItem[]>([newPromoItem()]);
  const [period, setPeriod] = useState('');

  const promoTemplate = promo ? templates.find((t) => t.enabled && t.country === promoLoc && isPromoTemplate(t.bodyHtml)) ?? null : null;

  useEffect(() => {
    if (!promo || !promoTemplate?.bodyHtml) return;
    setHtml(buildPromoHtml(promoTemplate.bodyHtml, promoItems, promoLang(promoLoc)));
    setTemplateId(promoTemplate.id);
  }, [promo, promoTemplate, promoItems, promoLoc]);

  useEffect(() => {
    if (!promo) return;
    const value = period.trim() ? `${PROMO_SUBJECT_PREFIX}${period.trim()}` : '';
    setSubject(value);
    setName(value);
  }, [promo, period]);

  useEffect(() => {
    if (!builderUsed || editorMode !== 'builder') return;
    setHtml((h) => applyBlocks(h, blocks));
    setValidation(null);
  }, [blocks, builderUsed, editorMode, templateId, step]);

  const changeBlocks: typeof setBlocks = (v) => { setBuilderUsed(true); setBlocks(v); };

  function changePromoLoc(loc: string) {
    setPromoLoc(loc);
    setSegmentIds((cur) => cur.filter((id) => segments.find((s) => s.id === id)?.country === loc));
  }

  const testEmailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(testEmail.trim());
  async function sendTest() {
    if (!testEmailValid || !senderId) return;
    setTestSending(true);
    setTestResult(null);
    try {
      const r = await api.testSend({ to: testEmail.trim(), html: composeBody(html, preheader), subject, senderId: Number(senderId) });
      setTestResult({ ok: true, msg: `Odesláno na ${r.to} (oslovení „${r.vocative}").` });
    } catch (e) {
      setTestResult({ ok: false, msg: e instanceof ApiError ? e.message : 'Odeslání selhalo' });
    } finally {
      setTestSending(false);
    }
  }

  useEffect(() => {
    if (!segmentIds.length) { setEstimate(null); return; }
    let cancelled = false;
    setEstimate(null);
    api.segmentEstimate(segmentIds)
      .then((r) => !cancelled && setEstimate(r))
      .catch(() => !cancelled && setEstimate(null));
    return () => { cancelled = true; };
  }, [segmentIds, api]);

  useFeather([step]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.segments(), api.senders(), api.templates()])
      .then(([segs, snd, tpl]) => {
        if (cancelled) return;
        setSegments(segs.filter((s) => s.isActive && countries.includes(s.country)).sort((a, b) => countries.indexOf(a.country) - countries.indexOf(b.country) || a.name.localeCompare(b.name, 'cs')));
        const usable = snd.filter((s) => s.active);
        setSenders(usable);
        setTemplates(tpl);
        setSenderId((cur) => (cur === '' ? usable.find((s) => s.isDefault)?.id ?? usable[0]?.id ?? '' : cur));
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [countries, api]);

  useEffect(() => {
    if (editId || !initialTemplateId) return;
    let cancelled = false;
    api.template(initialTemplateId)
      .then((t) => {
        if (cancelled || !t.bodyHtml) return;
        if (isPromoTemplate(t.bodyHtml)) {
          setPromo(true);
          setPromoLoc(t.country === 'SK' ? 'SK' : 'CZ');
          setTemplateId(t.id);
          setPreheader(t.preheader || (t.country === 'SK' ? 'Aktuálne promo novinky klientov Affilmastera' : 'Aktuální promo novinky klientů Affilmasteru'));
          return;
        }
        setTemplateId(t.id);
        setHtml(t.bodyHtml);
        if (t.subject) setSubject(t.subject);
        if (t.preheader) setPreheader(t.preheader);
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [initialTemplateId, editId, api]);

  useEffect(() => {
    if (!editId) return;
    let cancelled = false;
    api.campaign(editId)
      .then((d) => {
        if (cancelled) return;
        setName(d.name ?? '');
        setSubject(d.subject ?? '');
        const bm = (d.meta as { builder?: { blocks?: EmailBlock[] } } | null)?.builder;
        if (bm && Array.isArray(bm.blocks)) { setBlocks(bm.blocks); setBuilderUsed(true); setEditorMode('builder'); setMailTab('builder'); }
        else { setEditorMode('editor'); setMailTab('editor'); }
        const pm = (d.meta as { promo?: PromoMeta } | null)?.promo;
        if (pm && Array.isArray(pm.items)) {
          setPromo(true);
          setPromoLoc(pm.country === 'SK' ? 'SK' : 'CZ');
          setPeriod(pm.period ?? '');
          setPromoItems(pm.items.length ? pm.items : [newPromoItem()]);
        }
        if (d.senderId != null) setSenderId(d.senderId);
        if (d.segmentIds?.length) setSegmentIds(d.segmentIds);
        if (d.templateId != null) setTemplateId(d.templateId);
        if (d.htmlBody) { const sp = splitPreheader(d.htmlBody); setPreheader(sp.preheader); setHtml(sp.body); }
        if (d.scheduledAt) { setScheduleOn(true); setScheduledAt(toLocalInput(d.scheduledAt)); }
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [editId, api]);



  function applyDefaultTemplate() {
    if (promo || editId || templateId !== '' || html !== DEFAULT_HTML) return;
    const markets = [...new Set(segments.filter((x) => segmentIds.includes(x.id)).map((x) => x.country))];
    const market = markets.length === 1 ? markets[0] : 'CZ';
    const t = templates.find((x) => x.isDefault && x.country === market) ?? templates.find((x) => x.isDefault);
    if (!t?.bodyHtml) return;
    setTemplateId(t.id);
    setHtml(t.bodyHtml);
    if (t.preheader && !preheader) setPreheader(t.preheader);
    setValidation(null);
  }

  const canDraft = name.trim().length >= 2;
  const canStep1 = !!(segmentIds.length && name.trim() && subject.trim() && senderId !== '' && (!promo || (period.trim() && promoTemplate)));
  const promoProblems = promo ? promoIssues(promoItems) : [];
  const promoValidation = promo ? validateHtml(html) : null;
  const chosen = segments.filter((s) => segmentIds.includes(s.id));
  const chosenLabel = chosen.map((s) => `${FLAG[s.country] ?? ''} ${s.name}`.trim()).join(' + ');
  const toggleSegment = (id: number) => setSegmentIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const mailValidated = promo
    ? promoProblems.length === 0 && promoValidation?.level !== 'err'
    : validation?.level === 'ok' || validation?.level === 'warn';
  const visibleSegments = promo ? segments.filter((s) => s.country === promoLoc) : segments;

  const [copied, setCopied] = useState<string | null>(null);
  async function copyVar(token: string) {
    try {
      await navigator.clipboard.writeText(token);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = token;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      ta.remove();
    }
    setCopied(token);
    window.setTimeout(() => setCopied((c) => (c === token ? null : c)), 1500);
  }

  function insertVar(token: string) {
    const ta = htmlRef.current;
    if (!ta) { setHtml((h) => h + token); return; }
    const start = ta.selectionStart ?? html.length;
    const end = ta.selectionEnd ?? html.length;
    const next = html.slice(0, start) + token + html.slice(end);
    setHtml(next);
    setValidation(null);
    requestAnimationFrame(() => { ta.focus(); ta.selectionStart = ta.selectionEnd = start + token.length; });
  }

  async function save(schedule: boolean) {
    setSaving(true); setErr(null);
    try {
      const body = {
        name: name.trim(),
        segmentIds, senderId: senderId === '' ? null : Number(senderId),
        templateId: templateId === '' ? null : Number(templateId),
        subject: subject.trim(),
        htmlBody: composeBody(html, preheader),
        scheduledAt: scheduleOn && scheduledAt ? new Date(scheduledAt).toISOString() : null,
        ...(promo ? { meta: { promo: { country: promoLoc, period: period.trim(), items: promoItems } } } : builderUsed ? { meta: { builder: { blocks } } } : {}),
      };
      let id: number;
      if (editId) { await api.updateCampaign(editId, body); id = editId; }
      else { ({ id } = await api.createCampaign(body)); }
      if (schedule) await api.updateCampaign(id, { status: 'scheduled' });
      onDone();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Uložení selhalo');
      setSaving(false);
    }
  }

  const steps: { n: Step; label: string }[] = [
    { n: 1, label: 'Základní info' }, { n: 2, label: promo ? 'Promo obsah' : 'HTML obsah' }, { n: 3, label: 'Náhled a odeslání' },
  ];

  return (
    <section className="campaign-wizard">
      <style>{WZ_CSS}</style>
      <div className="campaign-wizard-head">
        <div><div className="prototype-eyebrow">{eyebrow} · KAMPANĚ</div><h1>{editId ? 'Upravit kampaň' : 'Nová kampaň'}</h1><p>{step === 1 ? 'Nastav publikum a způsob doručení.' : step === 2 ? 'Připrav html obsah a zkontroluj výsledek.' : 'Poslední kontrola před spuštěním.'}</p></div>
        <button type="button" className="btn btn-sm btn-link ms-auto" onClick={onDone}>Zrušit</button>
      </div>

      <div className="campaign-studio">
      <div className="wz-stepper mb-3" aria-label="Postup vytvoření kampaně">
        {steps.map((s, i) => (
          <div key={s.n} style={{ display: 'contents' }}>
            <div className={`wz-step${step === s.n ? ' active' : step > s.n ? ' done' : ''}`}>
              <div className="wz-step-num">{s.n}</div>
              <div className="wz-step-label">{s.label}</div>
            </div>
            {i < steps.length - 1 && <div className="wz-step-line" />}
          </div>
        ))}
      </div>
      <main className="campaign-studio-main">
      {err && <div className="alert alert-danger py-2">{err}</div>}

      {step === 1 && (
        <div className="card shadow-sm wz3-card campaign-setup"><div className="card-body p-3">
          <div className="campaign-step-head"><span>Krok 1</span><h2>Základ kampaně</h2><p>Vyber jednu nebo více skupin, kterým kampaň pošleme, a doplň obsah.</p></div>
          <div className="row g-2">
            {promo && <div className="col-12 campaign-choice-block"><label className="wz-lbl">Lokace <span className="text-danger">*</span> <span className="wz-hint">určuje jazyk e-mailu a nabídku skupin i klientů</span></label><div className="campaign-market-options">
              {['CZ', 'SK'].map((c) => <button key={c} type="button" className={promoLoc === c ? 'active' : ''} onClick={() => changePromoLoc(c)}>{FLAG[c]} {c}</button>)}
            </div>{!promoTemplate && templates.length > 0 && <div className="wz-validation err mt-2">Pro lokaci {promoLoc} chybí aktivní šablona Promo news.</div>}</div>}
            <div className="col-12 campaign-choice-block"><label className="wz-lbl">Skupiny <span className="text-danger">*</span> <span className="wz-hint">lze vybrat i víc, kontakty se sloučí bez duplicit</span></label><div className="campaign-market-options">
              {visibleSegments.map((s) => <button key={s.id} type="button" className={segmentIds.includes(s.id) ? 'active' : ''} onClick={() => toggleSegment(s.id)}>{FLAG[s.country] ?? ''} {s.name}</button>)}
              {visibleSegments.length === 0 && <span className="text-muted small">Žádné aktivní skupiny.</span>}
            </div></div>
            <div className="col-12 campaign-form-divider"><span>Obsah a cílení</span></div>
            {!promo && <div className="col-12"><label className="wz-lbl">Název kampaně <span className="text-danger">*</span> <span className="wz-hint">interní</span></label>
              <input className="form-control form-control-sm" value={name} onChange={(e) => setName(e.target.value)} placeholder="např. Novinky pro partnery 10/2026 - CZ" /></div>}
            {promo ? (
              <div className="col-12"><label className="wz-lbl">Předmět emailu <span className="text-danger">*</span> <span className="wz-hint">vyplň období, předmět i název kampaně bude „{PROMO_SUBJECT_PREFIX}{period.trim() || '…'}“</span></label>
                <div className="input-group input-group-sm"><span className="input-group-text">{PROMO_SUBJECT_PREFIX.trim()}</span>
                  <input className="form-control form-control-sm" value={period} onChange={(e) => setPeriod(e.target.value)} maxLength={120} placeholder="např. týden 39 nebo 22. - 28. 9." /></div></div>
            ) : (
              <div className="col-12"><label className="wz-lbl">Předmět emailu <span className="text-danger">*</span> <span className="wz-hint">lze použít <code>[% shared.VOCATIVE %]</code> <button type="button" className="wz-var-btn" onClick={() => void copyVar('[% shared.VOCATIVE %]')}>{copied === '[% shared.VOCATIVE %]' ? '✓ Zkopírováno' : 'Zkopírovat oslovení'}</button></span></label>
                <input className="form-control form-control-sm" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="např. Danieli, máme pro vás novou kampaň" /></div>
            )}
            <div className="col-12"><label className="wz-lbl">Preheader <span className="wz-hint">skrytý text v náhledu schránky (za předmětem); doporučeno ~40–100 znaků. Vloží se automaticky do emailu.</span></label>
              <input className="form-control form-control-sm" value={preheader} onChange={(e) => setPreheader(e.target.value)} maxLength={200} placeholder="např. Nové podmínky spolupráce a vyšší provize od listopadu" /></div>
            <div className="col-sm-6"><label className="wz-lbl">Odesílatel <span className="text-danger">*</span></label>
              <select className="form-select form-select-sm" value={senderId} onChange={(e) => setSenderId(e.target.value === '' ? '' : Number(e.target.value))} ><option value="">- Vyber -</option>{senders.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.email})</option>)}</select></div>
            <div className="col-12"><label className="form-check form-switch d-inline-flex align-items-center gap-2 mb-1" style={{ cursor: 'pointer' }}>
              <input className="form-check-input" type="checkbox" checked={scheduleOn} onChange={(e) => setScheduleOn(e.target.checked)} /><span className="wz-lbl mb-0" style={{ textTransform: 'none' }}>Zvolit datum a čas rozesílky</span></label>
              <div className="wz-hint mb-1">Pokud necháš vypnuté, odesílá se hned po dokončení wizardu.</div>
              {scheduleOn && <input type="datetime-local" className="form-control form-control-sm" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />}</div>
            {segmentIds.length > 0 && <div className="col-12"><div className="wz-recipients">Odhad příjemců: <strong>{estimate == null ? 'počítám…' : `${estimate.count.toLocaleString('cs-CZ')} kontaktů`}</strong> · {chosen.length > 1 ? 'skupiny' : 'skupina'} <strong>{chosenLabel}</strong>{estimate && estimate.unsubscribed > 0 ? <> · {estimate.unsubscribed.toLocaleString('cs-CZ')} odhlášených se přeskočí</> : null}</div>
              {estimate && estimate.missingVocative > 0 && <div className="wz-validation err mt-2">{estimate.missingVocative.toLocaleString('cs-CZ')} kontaktů nemá platný vokativ a mail nedostanou. Doplň vokativ na kartě kontaktu (detail skupiny ukáže koho).</div>}
            </div>}
          </div>
          <div className="d-flex justify-content-end gap-2 mt-3"><button type="button" className="btn btn-sm btn-outline-primary" disabled={!canDraft || saving} onClick={() => save(false)}>Uložit jako koncept</button><button type="button" className="btn btn-primary btn-sm" disabled={!canStep1} onClick={() => { applyDefaultTemplate(); setStep(2); }}>Pokračovat na obsah</button></div>
        </div></div>
      )}

      {step === 2 && promo && (
        <div className="card shadow-sm wz3-card" style={{ maxWidth: 1180 }}><div className="card-body">
          <div className="campaign-step-head"><span>Krok 2</span><h2>Promo obsah</h2><p>Vyber klienty, doplň text a odkaz na detail v promo centru. Hlavička, oslovení i patička jsou pevně dané.</p></div>
          <div className="promo-layout">
            <PromoNewsEditor country={promoLoc} items={promoItems} onChange={setPromoItems} />
            <iframe title="Náhled emailu" sandbox="allow-same-origin" srcDoc={renderPreview(html)} />
          </div>
          {promoProblems.length > 0 && <div className="wz-validation warn mt-3"><h6 className="mb-1">Doplň prosím</h6><ul>{promoProblems.map((m, i) => <li key={i}>{m}</li>)}</ul></div>}
          {promoProblems.length === 0 && promoValidation?.level === 'err' && <div className="wz-validation err mt-3"><ul>{promoValidation.messages.map((m, i) => <li key={i}>{m}</li>)}</ul></div>}
          <div className="d-flex justify-content-between mt-3">
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setStep(1)}>Zpět</button>
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-sm btn-outline-primary" disabled={!canDraft || saving} onClick={() => save(false)}>Uložit jako koncept</button>
              <button type="button" className="btn btn-primary btn-sm" disabled={!mailValidated} onClick={() => setStep(3)}>Pokračovat na náhled</button>
            </div>
          </div>
        </div></div>
      )}

      {step === 2 && !promo && (
        <div className="card shadow-sm wz3-card" style={{ maxWidth: 1180 }}><div className="card-body">
          <div className="campaign-step-head"><span>Krok 2</span><h2>HTML obsah</h2><p>Vlož obsah, doplň personalizaci a ověř technickou kvalitu.</p></div>
          <div className="campaign-mail-editor">
            {mailTab === 'kod' && <div className="wz-vars-toolbar mb-2">
              {VAR_GROUPS.map((g) => (
                <span key={g.label} className="d-inline-flex align-items-center gap-1" style={{ flexWrap: 'wrap' }}>
                  <span className="wz-vars-label">{g.label}:</span>
                  {g.vars.map((v) => <button key={v.token} type="button" className={`wz-var-btn ${g.cls ?? ''}`} title="Vložit do HTML na místo kurzoru" onClick={() => insertVar(v.token)}>{v.text}</button>)}
                </span>
              ))}
            </div>}
            {editorMode === 'editor' && <div className="d-flex gap-2 mb-2">
              {(['editor', 'kod', 'nahled'] as const).map((t) => (
                <button key={t} type="button" className={`btn btn-sm ${mailTab === t ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setMailTab(t)}>{t === 'editor' ? 'Editor' : t === 'kod' ? 'HTML kód' : 'Náhled'}</button>
              ))}
            </div>}
            {mailTab === 'builder' ? (
              hasEditableRegion(html) ? (
                <>
                  {!builderUsed && !!editId && <div className="email-visual-note">Stavebnice nahradí obsah e-mailu bloky, jakmile v ní něco změníš. Hlavička, oslovení a patička zůstanou.</div>}
                  <EmailBuilder upload={async (file) => (await api.uploadAsset({ contentBase64: await fileToDataUrl(file), mime: file.type })).url} blocks={blocks} onChange={changeBlocks} />
                </>
              ) : (
                <div className="email-visual-note">Vybraná šablona nemá vyznačenou oblast obsahu, stavebnici s ní nejde použít. Použij Editor nebo HTML kód, případně načti Základní šablonu.</div>
              )
            ) : mailTab === 'editor' ? (
              <EmailVisualEditor html={html} onChange={(h) => { setHtml(h); setValidation(null); }} />
            ) : mailTab === 'kod' ? (
              <textarea ref={htmlRef} className="form-control form-control-sm" rows={13} value={html} onChange={(e) => { setHtml(e.target.value); setValidation(null); }} style={{ fontFamily: 'ui-monospace,Menlo,monospace', fontSize: '.82rem' }} />
            ) : (
              <iframe title="Náhled emailu" sandbox="allow-same-origin" srcDoc={renderPreview(html)} style={{ width: '100%', height: 360, border: '1px solid var(--bs-border-color)', borderRadius: 8, background: '#fff' }} />
            )}
            {validation && (
              <div className={`wz-validation ${validation.level} mb-2`}>
                <h6 className="mb-1">{validation.level === 'ok' ? '✓ Validace OK' : validation.level === 'warn' ? '⚠ Upozornění' : '✕ Chyby'}</h6>
                <ul>{validation.messages.map((m, i) => <li key={i}>{m}</li>)}</ul>
              </div>
            )}
          </div>
          <div className="d-flex justify-content-between mt-3">
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setStep(1)}>Zpět</button>
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-sm btn-outline-primary" disabled={!canDraft || saving} onClick={() => save(false)}>Uložit jako koncept</button>
              <button type="button" className="btn btn-primary btn-sm" onClick={() => {
                const issues = builderUsed ? blockIssues(blocks) : [];
                const v = validateHtml(html);
                if (issues.length) { setValidation({ level: 'err', messages: issues }); return; }
                setValidation(v);
                if (v.level !== 'err') setStep(3);
              }}>Pokračovat na náhled</button>
            </div>
          </div>
        </div></div>
      )}

      {step === 3 && (
        <div className="card shadow-sm wz3-card"><div className="card-body">
          <div className="campaign-step-head"><span>Krok 3</span><h2>Kontrola a spuštění</h2><p>Zkontroluj cílení, obsah a technickou připravenost kampaně.</p></div>
          <div className="wz3-card-head"><div className="wz3-card-title"><span className="feather-icon"><i data-feather="eye" style={{ width: 16, height: 16 }} /></span> Souhrn kampaně</div></div>
          <dl className="wz-summary mb-3">
            <dt>Název</dt><dd>{name}</dd>
            <dt>Kanál</dt><dd>📧 E-mail</dd>
            <dt>Předmět</dt><dd>{subject}</dd>
            <dt>Odesílatel</dt><dd>{(() => { const s = senders.find((x) => x.id === senderId); return s ? `${s.name} <${s.email}>` : '-'; })()}</dd>
            <dt>{chosen.length > 1 ? 'Skupiny' : 'Skupina'}</dt><dd>{chosenLabel || '-'}{estimate ? ` · ${estimate.count.toLocaleString('cs-CZ')} kontaktů` : ''}</dd>
            <dt>Čas rozesílky</dt><dd>{scheduleOn && scheduledAt ? new Date(scheduledAt).toLocaleString('cs-CZ') : 'ihned po spuštění'}</dd>
          </dl>

          {(() => {
            const sp = spamAudit(subject, composeBody(html, preheader));
            const barColor = sp.level === 'excellent' ? '#22c55e' : sp.level === 'ok' ? '#f59e0b' : '#ef4444';
            return (
              <div className="card shadow-sm mb-3"><div className="card-body">
                <div className="wz3-card-head"><div className="wz3-card-title"><span className="feather-icon"><i data-feather="shield" style={{ width: 16, height: 16 }} /></span> Spam audit</div><span className="wz-hint">{sp.verdict}</span></div>
                <div className="d-flex align-items-center gap-3 mb-3">
                  <div style={{ fontSize: '1.6rem', fontWeight: 700, color: barColor }}>{sp.score}</div>
                  <div style={{ flex: 1, height: 10, background: '#fff', borderRadius: 5, overflow: 'hidden', border: '1px solid rgba(0,0,0,.05)' }}>
                    <div style={{ height: '100%', width: `${Math.min(100, (sp.score / 10) * 100)}%`, background: barColor }} />
                  </div>
                  <div className="small fw-semibold" style={{ minWidth: 140, textAlign: 'right', color: barColor }}>{sp.verdict}</div>
                </div>
                <div className="d-flex flex-column gap-2">
                  {sp.cats.map((c) => {
                    const isKw = c.name === 'Spam keywords';
                    const foundSet = isKw
                      ? new Set(c.details.map((d) => d.replace(/\s*\(\d+×\)\s*$/, '')).filter((d) => d !== 'Žádná spam slova'))
                      : null;
                    return (
                    <div key={c.name} className="d-flex align-items-start gap-2" style={{ padding: '8px 10px', borderRadius: 6, background: 'var(--bs-tertiary-bg)', border: '1px solid var(--bs-border-color-translucent)' }}>
                      <span style={{ flexShrink: 0, width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.74rem', color: '#fff', fontWeight: 700, background: c.level === 'ok' ? '#22c55e' : c.level === 'warn' ? '#f59e0b' : '#dc2626' }}>{c.level === 'ok' ? '✓' : '!'}</span>
                      <div style={{ flex: 1 }}>
                        <div className="small fw-semibold">{c.name}</div>
                        {c.details.map((d, di) => <div key={di} className="text-muted" style={{ fontSize: '.74rem' }}>{d}</div>)}
                        {isKw && (
                          <>
                            <button type="button" className="btn btn-link btn-sm p-0" style={{ fontSize: '.72rem', textDecoration: 'none' }} onClick={() => setShowSpamWords((v) => !v)}>
                              {showSpamWords ? '▾ skrýt kontrolovaná slova' : '▸ co se kontroluje?'}
                            </button>
                            {showSpamWords && (
                              <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                {SPAM_KEYWORDS.map((kw) => {
                                  const hit = foundSet?.has(kw);
                                  return <span key={kw} style={{ fontSize: '.68rem', padding: '1px 7px', borderRadius: 999, background: hit ? '#fde2e1' : 'var(--bs-body-bg)', color: hit ? '#b42318' : 'var(--bs-secondary-color)', fontWeight: hit ? 700 : 400, border: `1px solid ${hit ? '#f4b9b5' : 'var(--bs-border-color-translucent)'}` }}>{kw}</span>;
                                })}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      <div className="small fw-semibold" style={{ whiteSpace: 'nowrap', color: c.score > 0 ? '#a35a00' : 'var(--bs-secondary-color)' }}>+{c.score}</div>
                    </div>
                    );
                  })}
                </div>
              </div></div>
            );
          })()}

          <div className="card shadow-sm mb-3"><div className="card-body">
            <div className="wz3-card-head"><div className="wz3-card-title"><span className="feather-icon"><i data-feather="eye" style={{ width: 16, height: 16 }} /></span> Náhled emailu</div>
              <div className="btn-group btn-group-sm">
                <button type="button" className={`btn ${prevDevice === 'desktop' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setPrevDevice('desktop')}>Desktop</button>
                <button type="button" className={`btn ${prevDevice === 'mobil' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setPrevDevice('mobil')}>Mobil</button>
              </div>
            </div>
            <div className="d-flex justify-content-center"><iframe title="Náhled" sandbox="allow-same-origin" srcDoc={renderPreview(html)} style={{ width: prevDevice === 'desktop' ? '100%' : 375, height: 420, border: '1px solid var(--bs-border-color)', borderRadius: 8, background: '#fff', transition: 'width .2s' }} /></div>
          </div></div>

          <div className="card shadow-sm mb-3"><div className="card-body py-2 px-3">
            <div className="wz3-card-title mb-2"><span className="feather-icon"><i data-feather="send" style={{ width: 14, height: 14 }} /></span> Test odeslání</div>
            <div className="d-flex gap-2 align-items-center">
              <input className="form-control form-control-sm" style={{ maxWidth: 280 }} placeholder="email pro test" value={testEmail} onChange={(e) => { setTestEmail(e.target.value); setTestResult(null); }} />
              <button type="button" className="btn btn-sm btn-outline-secondary" disabled={!testEmailValid || !senderId || testSending} onClick={sendTest} title={!senderId ? 'Nejdřív vyber odesílatele (krok 1)' : 'Odeslat testovací email'}>{testSending ? 'Odesílám…' : 'Odeslat test'}</button>
            </div>
            {testResult ? (
              <div className={`small mt-1 ${testResult.ok ? 'text-success' : 'text-danger'}`} style={{ fontSize: '.72rem' }}>{testResult.ok ? '✓ ' : '✕ '}{testResult.msg}</div>
            ) : (
              <div className="text-muted small mt-1" style={{ fontSize: '.7rem' }}>Předmět dostane prefix <code>[TEST]</code>. Vokativ se vezme z karty kontaktu nebo ze skupiny Test email, bez vokativu test nejde odeslat.</div>
            )}
          </div></div>

          <div className="d-flex justify-content-between">
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setStep(2)} disabled={saving}>Zpět</button>
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => save(false)} disabled={saving}>Uložit jako koncept</button>
              <button type="button" className="btn btn-sm btn-primary" onClick={() => save(true)} disabled={saving}>{saving ? 'Ukládám…' : scheduleOn && scheduledAt ? 'Naplánovat rozesílku' : 'Odeslat ihned'}</button>
            </div>
          </div>
        </div></div>
      )}
      </main>
      </div>
    </section>
  );
}

export function MailingCampaignTypeChoice({ onPick, onClose }: { onPick: (mode: EditorMode) => void; onClose: () => void }) {
  useFeather([]);
  return (
    <div className="campaign-type-backdrop" onClick={onClose}>
      <div className="card shadow campaign-type-dialog marketing-type-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="campaign-type-head"><div><span>Nová kampaň</span><h2>Jak chceš e-mail připravit?</h2><p>Publikum, předmět a čas rozesílky nastavíš v dalším kroku.</p></div><button type="button" className="btn-close" aria-label="Zavřít" onClick={onClose} /></div>
        <div className="campaign-type-list">
          <button type="button" className="campaign-type-option" onClick={() => onPick('builder')}><span className="campaign-type-icon"><i data-feather="grid" /></span><span><strong>Jednorázová kampaň · Drag &amp; Drop</strong><small>Poskládáš e-mail z bloků: nadpis, text, obrázek, tlačítko.</small></span></button>
          <button type="button" className="campaign-type-option" onClick={() => onPick('editor')}><span className="campaign-type-icon"><i data-feather="edit-3" /></span><span><strong>Jednorázová kampaň · WYSIWYG / HTML</strong><small>Píšeš přímo do e-mailu, nebo vložíš hotové HTML.</small></span></button>
          <div className="campaign-type-option disabled"><span className="campaign-type-icon"><i data-feather="git-branch" /></span><span><strong>Automatizovaný scénář <em>Připravujeme</em></strong><small>Vícekroková komunikace řízená chováním kontaktu.</small></span></div>
        </div>
      </div>
    </div>
  );
}
